import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import useModules from '../../lib/useModules';
import useMirrorPresence from '../../hooks/useMirrorPresence';
import WhatsNewBadge from './WhatsNewBadge';
import BuildIdLine from './BuildIdLine';
import { navGroups, getGroupForPath, stripeForPath as _stripeForPath } from './sidebarNav';


export const Sidebar = ({ isCollapsed, setIsCollapsed }) => {
  const { isOrgAdmin, isSuperAdmin } = useAuth();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const c = (dark, light) => (isDark ? dark : light);
  const borderCls = c('border-[#1f2a44]', 'border-slate-200');
  const logoFilter = c('brightness-0 invert opacity-90', '');
  const location = useLocation();
  const { isEnabled } = useModules();
  const { active: mirrorActive } = useMirrorPresence();
  const onMirror = location.pathname.startsWith('/mirror');
  const showMirrorPill = mirrorActive && !onMirror;

  const visibleNavGroups = navGroups.filter(group => {
    if (group.requiresAdmin && !isOrgAdmin) return false;
    if (group.moduleId && !isEnabled(group.moduleId)) return false;
    return true;
  }).map(group => ({
    ...group,
    items: group.items.filter(item => {
      if (item.superAdminOnly && !isSuperAdmin) return false;
      if (item.moduleId && !isEnabled(item.moduleId)) return false;
      return true;
    })
  }));

  // All collapsed by default, auto-expand the group containing the active route
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const initial = {};
    navGroups.forEach(group => { initial[group.id] = false; });
    const activeGroup = getGroupForPath(location.pathname);
    initial[activeGroup] = true;
    return initial;
  });

  // Auto-expand when navigating to a page in a different group
  useEffect(() => {
    const activeGroup = getGroupForPath(location.pathname);
    setExpandedGroups(prev => {
      if (prev[activeGroup]) return prev;
      return { ...prev, [activeGroup]: true };
    });
  }, [location.pathname]);

  const toggleGroup = (groupId) => {
    if (isCollapsed) return;
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-full border-r z-50 transition-all duration-300 flex flex-col theme-surface',
        c('bg-[#0a0f1c] text-slate-300', 'bg-white text-slate-700'),
        borderCls,
        isCollapsed ? 'w-16' : 'w-64'
      )}
      data-theme-surface={resolvedTheme}
    >
      {/* Brand */}
      <div className={cn('flex items-center justify-between h-16 px-4 border-b flex-shrink-0', borderCls)}>
        {!isCollapsed && (
          <img
            src="/seertech_brand_lockup.png"
            alt="Seertech · Mission Control"
            className={cn('h-7 w-auto', logoFilter)}
            data-testid="sidebar-brand-logo"
          />
        )}
        {isCollapsed && (
          <img
            src="/seertech_monogram.png"
            alt="Seertech"
            className={cn('h-9 w-9 mx-auto object-contain', logoFilter)}
            data-testid="sidebar-brand-icon-collapsed"
          />
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={cn('p-1.5 rounded-md transition-colors flex-shrink-0', c('hover:bg-white/5 text-slate-400', 'hover:bg-slate-100 text-slate-500'))}
          data-testid="sidebar-toggle-btn"
        >
          <ChevronLeft
            className={cn(
              'h-5 w-5 transition-transform',
              isCollapsed && 'rotate-180'
            )}
          />
        </button>
      </div>

      {/* Mirror presence pill — visible only when a Mirror tab is open
          elsewhere AND the user is currently on Mission Control. Clicking
          jumps them straight into Mirror. */}
      {showMirrorPill && (
        <Link
          to="/mirror"
          className={cn(
            'group flex items-center gap-2 mx-3 mt-3 px-2.5 py-1.5 rounded-full',
            'border border-indigo-200 bg-gradient-to-r from-indigo-50 to-cyan-50',
            'text-indigo-800 hover:from-indigo-100 hover:to-cyan-100 transition-colors',
            isCollapsed ? 'justify-center px-0' : ''
          )}
          title="Mirror is open in another tab — click to open in this one"
          data-testid="sidebar-mirror-presence-pill"
        >
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500" />
          </span>
          {!isCollapsed && (
            <span className="text-[11px] font-semibold uppercase tracking-wider">
              Mirror is open
            </span>
          )}
        </Link>
      )}

      {/* Navigation */}
      <nav className={cn('flex-1 overflow-y-auto py-2 scrollbar-thin scrollbar-track-transparent', c('scrollbar-thumb-slate-700', 'scrollbar-thumb-slate-300'))}>
        {visibleNavGroups.map((group, groupIndex) => {
          const isExpanded = expandedGroups[group.id];
          const hasActiveChild = group.items.some(item =>
            item.path && (
              location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path))
            )
          );

          return (
            <div key={group.id}>
              {groupIndex > 0 && (
                <div className={cn('mx-3 my-1.5 border-t', borderCls)} />
              )}

              {!isCollapsed ? (
                group.directPath ? (
                  <NavLink
                    to={group.directPath}
                    end={group.directPath === '/'}
                    className={({ isActive }) => cn(
                      'w-full flex items-center gap-3 px-3 py-2 transition-all text-left group',
                      c('text-slate-400 hover:text-white', 'text-slate-500 hover:text-slate-900'),
                      (isActive || hasActiveChild) && c('text-white', 'text-slate-900'),
                    )}
                    data-testid={`nav-group-${group.id}`}
                  >
                    <group.icon className={cn(
                      'h-4.5 w-4.5 flex-shrink-0 transition-colors',
                      hasActiveChild ? 'text-[#44b6d2]' : c('text-slate-500 group-hover:text-slate-300', 'text-slate-400 group-hover:text-slate-600')
                    )} />
                    <span className="text-xs font-bold uppercase tracking-wider">{group.label}</span>
                  </NavLink>
                ) : (
                <button
                  onClick={() => toggleGroup(group.id)}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2 transition-all text-left group',
                    c('text-slate-400 hover:text-white', 'text-slate-500 hover:text-slate-900'),
                    hasActiveChild && c('text-white', 'text-slate-900'),
                  )}
                  data-testid={`nav-group-${group.id}`}
                >
                  <div className="flex items-center gap-3">
                    <group.icon className={cn(
                      'h-4.5 w-4.5 flex-shrink-0 transition-colors',
                      hasActiveChild ? 'text-[#44b6d2]' : c('text-slate-500 group-hover:text-slate-300', 'text-slate-400 group-hover:text-slate-600')
                    )} />
                    <span className="text-xs font-bold uppercase tracking-wider">{group.label}</span>
                  </div>
                  <ChevronRight className={cn(
                    'h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 text-slate-500',
                    isExpanded && 'rotate-90'
                  )} />
                </button>
                )
              ) : (
                group.directPath ? (
                  <Link to={group.directPath} className="flex justify-center py-1.5" title={group.label}>
                    <group.icon className={cn(
                      'h-5 w-5',
                      hasActiveChild ? 'text-[#44b6d2]' : c('text-slate-500', 'text-slate-400')
                    )} />
                  </Link>
                ) : (
                <div className="flex justify-center py-1.5">
                  <group.icon className={cn(
                    'h-5 w-5',
                    hasActiveChild ? 'text-[#44b6d2]' : c('text-slate-500', 'text-slate-400')
                  )} title={group.label} />
                </div>
                )
              )}

              <div
                aria-hidden={!isCollapsed && !isExpanded}
                className={cn(
                  'overflow-hidden transition-all duration-200',
                  !isCollapsed && !isExpanded && 'max-h-0 opacity-0 pointer-events-none',
                  !isCollapsed && isExpanded && 'max-h-[800px] opacity-100',
                  isCollapsed && 'max-h-[800px]'
                )}
              >
                {!group.directPath && group.items.map((item, idx) => {
                  // Sub-headers are layout-only — render a small
                  // uppercase label as a tier divider (e.g. "Health
                  // Dashboards", "Diagnostics") within an expanded
                  // group. Skipped entirely when the rail is
                  // collapsed (no room for the text).
                  if (item.subHeader) {
                    if (isCollapsed) return null;
                    return (
                      <p
                        key={`subhead-${group.id}-${idx}`}
                        className="px-3 mt-3 mb-1 ml-5 text-[10px] font-semibold uppercase tracking-widest text-slate-400"
                        data-testid={`nav-subheader-${item.subHeader.toLowerCase().replace(/[&\s]+/g, '-')}`}
                      >
                        {item.subHeader}
                      </p>
                    );
                  }
                  const stripe = _stripeForPath(item.path);
                  return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    data-testid={`nav-${item.label.toLowerCase().replace(/[&\s]+/g, '-')}`}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-md px-3 py-1.5 transition-all border-l-[3px] border-l-transparent',
                        c('text-slate-400 hover:text-white hover:bg-white/5', 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'),
                        isActive && c('bg-white/[0.06] text-white ring-[#22304d]', 'bg-slate-100 text-slate-900 ring-slate-200'),
                        isActive && 'font-medium shadow-sm ring-1',
                        isActive && stripe,
                        isCollapsed && 'justify-center mx-2 my-0.5',
                        !isCollapsed && 'ml-5 mr-2 my-0.5'
                      )
                    }
                    title={isCollapsed ? item.label : undefined}
                  >
                    <item.icon className="h-4 w-4 flex-shrink-0" />
                    {!isCollapsed && (
                      <span className="text-sm flex items-center gap-1.5">
                        {item.label}
                        {item.hint && (
                          <span
                            className={cn('text-[9px] font-semibold uppercase tracking-wider rounded px-1 py-[1px] leading-none', c('text-slate-400 bg-white/5', 'text-slate-500 bg-slate-100'))}
                            title={`Internal engine: ${item.hint}`}
                            data-testid={`nav-hint-${item.hint.toLowerCase()}`}
                          >
                            {item.hint}
                          </span>
                        )}
                      </span>
                    )}
                  </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div
        className={cn(
          'flex-shrink-0 border-t',
          borderCls,
          isCollapsed ? 'py-2' : 'px-3 py-3'
        )}
      >
        <WhatsNewBadge isCollapsed={isCollapsed} />
      </div>
      {!isCollapsed && (
        <div className={cn('flex-shrink-0 p-4 border-t', borderCls)}>
          <div className="text-xs text-slate-500">
            <img
              src="/seertech_full_lockup.png"
              alt="Seertech · Mission Critical Learning"
              className={cn('h-14 w-auto mb-1.5', c('brightness-0 invert opacity-80', ''))}
              data-testid="sidebar-footer-logo"
            />
            <BuildIdLine />
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
